import {HubriseCatalogConverterContract} from 'common/api/hubrise/service/HubriseCatalogConverterContract';
import {HubriseMenuCatalogContract} from 'common/api/hubrise/model/menu/HubriseMenuCatalogContract';
import {HubriseMenuProductContract} from 'common/api/hubrise/model/menu/HubriseMenuProductContract';
import {HubriseMenuCategoryContract} from 'common/api/hubrise/model/menu/HubriseMenuCategoryContract';
import {HubriseOrderEditableItemContract} from 'common/api/hubrise/model/order/HubriseOrderEditableItemContract';
import {HubriseOrderEditableOptionContract} from 'common/api/hubrise/model/order/HubriseOrderEditableOptionContract';
import {HubriseMenuProductOptionListContract} from 'common/api/hubrise/model/menu/HubriseMenuProductOptionListContract';
import {isNullOrUndefined} from 'common/util/HelperUtil';
import {HubriseMenuProductOptionContract} from 'common/api/hubrise/model/menu/HubriseMenuProductOptionContract';
import {ObjectUtil} from 'common/util/ObjectUtil';
import {HubriseMenuVariantContract} from 'common/api/hubrise/model/menu/HubriseMenuVariantContract';
import {HubriseRestrictionEnforcer} from 'common/api/hubrise/service/HubriseRestrictionEnforcer';
import {HubriseRestrictionEnforcerContract} from 'common/api/hubrise/service/HubriseRestrictionEnforcerContract';
import {HubriseMenuSkuContract} from 'common/api/hubrise/model/menu/HubriseMenuSkuContract';
import {HubriseMenuPriceOverrideContract} from 'common/api/hubrise/model/menu/HubriseMenuPriceOverrideContract';

export class HubriseCatalogConverter implements HubriseCatalogConverterContract {
    private readonly hubriseRestrictionEnforcer: HubriseRestrictionEnforcerContract;

    public constructor() {
        this.hubriseRestrictionEnforcer = new HubriseRestrictionEnforcer();
    }

    public editableItems(catalog: HubriseMenuCatalogContract, date: string, variant?: HubriseMenuVariantContract): {
        withRef: HubriseOrderEditableItemContract[],
        withName: HubriseOrderEditableItemContract[]
    } {
        if (!Array.isArray(catalog?.data?.products)) {
            return {withRef: [], withName: []};
        }

        const categories: Record<string, string> = {};
        if (Array.isArray(catalog.data.categories)) {
            catalog.data.categories.forEach((category: HubriseMenuCategoryContract) => {
                categories[category.id] = category.name;
            })
        }
        const items: HubriseOrderEditableItemContract[] = [];
        catalog.data.products.forEach((p: HubriseMenuProductContract) => {
            this.hubriseRestrictionEnforcer.filter<HubriseMenuSkuContract>(p.skus, date, variant).forEach(sku => {
                items.push(ObjectUtil.compact({
                    id: sku.id,
                    sku_ref: sku.ref ?? undefined,
                    category_name: categories[p.category_id],
                    product_name: [p.name, sku.name].filter(part => part !== null).join(' - '),
                    quantity: '1',
                    tax_rate: p.tax_rate ?? undefined,
                    price: this.selectPrice({price: sku.price, price_overrides: sku.price_overrides}, date, variant)
                }));
            });

            // const reference: string | null | undefined = p.ref ??
            //     (p.skus?.length === 1 && (!Array.isArray(p.skus[0].option_list_ids) || p.skus[0].option_list_ids.length === 0) ? p.skus[0].ref : null);
            // const price: string | null = p.skus?.length === 1 && (!Array.isArray(p.skus[0].option_list_ids) || p.skus[0].option_list_ids.length === 0)
            //     ? p.skus[0].price : HubrisePriceUtil.multiply(p.skus[0].price, 0);
            //
            // return {id: p.id, sku_ref: reference, category_name: categories[p.category_id], product_name: p.name, quantity: '1', tax_rate: p.tax_rate, price};
        })
        items.sort((a: HubriseOrderEditableItemContract, b: HubriseOrderEditableItemContract) =>
            a.category_name?.localeCompare(b.category_name ?? '') || a.product_name?.localeCompare(b.product_name ?? ''))

        return {withRef: items.filter(i => !isNullOrUndefined(i.sku_ref)), withName: items};
    }

    public editableItemOptions(catalog: HubriseMenuCatalogContract, date: string, variant?: HubriseMenuVariantContract): {
        withRef: Record<string, HubriseOrderEditableOptionContract[]>,
        withName: Record<string, HubriseOrderEditableOptionContract[]>
    } {
        if (!Array.isArray(catalog?.data?.products)) {
            return {withRef: {}, withName: {}};
        }

        const optionLists: Record<string, HubriseMenuProductOptionListContract> = {}
        if (Array.isArray(catalog.data.option_lists)) {
            catalog.data.option_lists.forEach((o: any) => {
                optionLists[o.id] = o;
            });
        }

        const editableItemOptionNames: Record<string, HubriseOrderEditableOptionContract[]> = {};
        catalog.data.products.forEach((p: HubriseMenuProductContract) => {
            this.hubriseRestrictionEnforcer.filter<HubriseMenuSkuContract>(p.skus, date, variant).forEach(sku => {
                if (Array.isArray(sku.option_list_ids) && sku.option_list_ids.length > 0) {
                    editableItemOptionNames[sku.id] = [];
                    sku.option_list_ids.forEach((optionListId: string) => {
                        const optionList: HubriseMenuProductOptionListContract = optionLists[optionListId]
                        if (optionList === undefined)
                            return // TODO log

                        if (!Array.isArray(optionList.options) || optionList.options.length === 0)
                            return // TODO log

                        this.hubriseRestrictionEnforcer.filter<HubriseMenuProductOptionContract>(optionList.options, date, variant)
                            .forEach((option: HubriseMenuProductOptionContract) => {
                                editableItemOptionNames[sku.id].push(ObjectUtil.compact({
                                    id: String(option.id),
                                    ref: option.ref ?? optionList.ref ?? null,
                                    option_list_name: optionList.name,
                                    name: option.name,
                                    quantity: 1,
                                    price: this.selectPrice({price: option.price, price_overrides: option.price_overrides}, date, variant)
                                }));
                            });
                    });
                    editableItemOptionNames[sku.id].sort((a: HubriseOrderEditableOptionContract, b: HubriseOrderEditableOptionContract) =>
                        a.option_list_name?.localeCompare(b.option_list_name ?? '') || a.name?.localeCompare(b.name ?? ''));
                }
            });

            // editableItemOptionNames[p.id] = []
            // if (!Array.isArray(p.skus) || p.skus.length === 0 || !Array.isArray(p.skus[0].option_list_ids) || p.skus[0].option_list_ids.length === 0)
            //     return
            //
            // p.skus.forEach((sku: any) => {
            //     if (Array.isArray(sku.option_list_ids) && sku.option_list_ids.length > 0) {
            //         sku.option_list_ids.forEach((optionListId: any) => {
            //             const optionList: any = optionLists[optionListId]
            //             if (optionList === undefined)
            //                 return // TODO log
            //
            //             if (!Array.isArray(optionList.options) || optionList.options.length === 0)
            //                 return // TODO log
            //
            //             optionList.options.forEach((option: any) => {
            //                 const price: string = HubrisePriceUtil.sum([sku.price ?? null, option.price ?? null])
            //
            //                 editableItemOptionNames[p.id].push({
            //                     id: String(sku.id) + '-' + String(optionList.id) + '-' + String(option.id),
            //                     ref: option.ref ?? null,
            //                     option_list_name: optionList.name,
            //                     name: option.name,
            //                     quantity: 1,
            //                     price
            //                 })
            //             })
            //         })
            //     } else {
            //         const price: string = sku.price ?? null
            //
            //         editableItemOptionNames[p.id].push({id: sku.id, ref: sku.ref ?? null, option_list_name: '', name: sku.name, quantity: 1, price});
            //     }
            // })
            // editableItemOptionNames[p.id].sort(
            //     (a: HubriseOrderEditableOptionContract, b: HubriseOrderEditableOptionContract) => a.name?.localeCompare(b.name ?? ''))
        });

        const editableItemOptionRefs: Record<string, HubriseOrderEditableOptionContract[]> = {}
        Object.keys(editableItemOptionNames).forEach(itemId => {
            editableItemOptionRefs[itemId] = editableItemOptionNames[itemId]?.filter(o => !isNullOrUndefined(o.ref)) ?? [];
        });

        return {
            withRef: editableItemOptionRefs,
            withName: editableItemOptionNames
        };
    }

    private selectPrice(data: { price: string, price_overrides: HubriseMenuPriceOverrideContract[] }, date: string, variant: HubriseMenuVariantContract): string {
        if (Array.isArray(data.price_overrides)) {
            const prices: HubriseMenuPriceOverrideContract[] = this.hubriseRestrictionEnforcer.filter(
                data.price_overrides?.map(override => {
                    return {price: override.price, restrictions: override};
                }), date, variant);

            if (prices.length > 0) {
                return prices[0].price;
            }
        }

        return data.price ?? null;
    }
}
